import { Dispatch, SetStateAction, useMemo } from "react";

export type FilterHandler<TFilter> = {
	filteringFor<TProperty extends StringKeyOf<TFilter>>(propertyName: TProperty): FilterValueState<TFilter[TProperty]>;
};

export function useFilterHandler<TFilter>(filter: TFilter, setFilter: Dispatch<SetStateAction<TFilter>>): FilterHandler<TFilter> {
	return useMemo(
		(): FilterHandler<TFilter> => {
			const filterMap: Map<StringKeyOf<TFilter>, FilterValueState<unknown>> = new Map();

			return {
				filteringFor<TProperty extends StringKeyOf<TFilter>>(propertyName: TProperty): FilterValueState<TFilter[TProperty]> {
					if (filterMap.has(propertyName)) {
						return (filterMap.get(propertyName)!) as FilterValueState<TFilter[TProperty]>;
					}

					const newState: FilterValueState<TFilter[TProperty]> = {
						value: filter[propertyName],
						setValue(newValue: TFilter[TProperty]): void {
							setFilter(filter => ({
								...filter,
								[propertyName]: newValue
							}));
						}
					};

					filterMap.set(propertyName, newState);

					return newState;
				}
			};
		},
		[filter, setFilter]
	);
}

export type FilterValueState<TValue> = {
	value: TValue;
	setValue(newValue: TValue): void;
};